import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Button, Container, Heading, Text, Input, Select, 
  Image, SimpleGrid, useToast, FormControl, FormLabel,
  Flex, Badge, Divider, IconButton, Modal, ModalOverlay,
  ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, useDisclosure
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, AddIcon } from '@chakra-ui/icons';
import supabase from '../utils/supabaseUtils';
import '../styles/AdminPlaceholderUpload.css';

const AdminPlaceholderUpload = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [placeholders, setPlaceholders] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState('');
  const fileInputRef = useRef(null);
  const toast = useToast();
  
  // Modal state for deleting images
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [imageToDelete, setImageToDelete] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchPlaceholders();
  }, []);

  // Fetch the athlete folder categories
  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase.storage
        .from('playmakerpics-images')
        .list('athletes', { sortBy: { column: 'name', order: 'asc' } });
        
      if (error) throw error;
      
      // Filter to only include directories (not files)
      const categoryFolders = data
        .filter(item => !item.name.includes('.') && item.name !== '.emptyFolderPlaceholder')
        .map(folder => folder.name);
        
      setCategories(categoryFolders);
      
      if (categoryFolders.length > 0 && !selectedCategory) {
        setSelectedCategory(categoryFolders[0]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: 'Error fetching categories',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Fetch placeholders for each category
  const fetchPlaceholders = async () => {
    setIsLoading(true);
    try {
      const { data: folderData, error: folderError } = await supabase.storage
        .from('playmakerpics-images')
        .list('athletes', { sortBy: { column: 'name', order: 'asc' } });
        
      if (folderError) throw folderError;
      
      // Get all folders (categories)
      const categoryFolders = folderData
        .filter(item => !item.name.includes('.') && item.name !== '.emptyFolderPlaceholder');
      
      // Also look for any images directly in the athletes folder
      const rootImages = folderData.filter(item => /\.(jpg|jpeg|png|gif)$/i.test(item.name));
      
      const result = {};
      
      // Process root images
      if (rootImages.length > 0) {
        result['General'] = await processImageFiles(rootImages, 'athletes');
      }
      
      // Process each category folder
      for (const folder of categoryFolders) {
        const { data: files, error } = await supabase.storage
          .from('playmakerpics-images')
          .list(`athletes/${folder.name}`);
          
        if (error) {
          console.error(`Error listing files in ${folder.name}:`, error);
          continue;
        }
        
        const categoryImages = files.filter(file => /\.(jpg|jpeg|png|gif)$/i.test(file.name));
        if (categoryImages.length > 0) {
          result[folder.name] = await processImageFiles(categoryImages, `athletes/${folder.name}`);
        } else {
          result[folder.name] = [];
        }
      }
      
      setPlaceholders(result);
    } catch (error) {
      console.error('Error fetching placeholders:', error);
      toast({
        title: 'Error fetching placeholders',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Process image files into a standard format
  const processImageFiles = async (files, basePath) => {
    const images = [];
    
    for (const file of files) {
      try {
        const path = `${basePath}/${file.name}`;
        const { data } = supabase.storage
          .from('playmakerpics-images')
          .getPublicUrl(path);
          
        if (data && data.publicUrl) {
          images.push({
            name: file.name,
            path: path,
            url: data.publicUrl,
            created_at: file.created_at || new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error(`Error processing file ${file.name}:`, error);
      }
    }
    
    return images;
  };

  // Create a new category
  const createCategory = async () => {
    if (!newCategory.trim()) {
      toast({
        title: 'Category name required',
        description: 'Please enter a valid category name',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Normalize category name to lowercase with no spaces
    const normalizedCategory = newCategory.trim().toLowerCase().replace(/\s+/g, '_');
    
    if (categories.includes(normalizedCategory)) {
      toast({
        title: 'Category exists',
        description: 'This category already exists',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    try {
      // Create a placeholder file to make the folder
      const { error } = await supabase.storage
        .from('playmakerpics-images')
        .upload(`athletes/${normalizedCategory}/.placeholder`, new Blob([''], { type: 'text/plain' }));
        
      if (error) throw error;
      
      toast({
        title: 'Category created',
        description: `Created category: ${newCategory}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      setNewCategory('');
      setSelectedCategory(normalizedCategory);
      await fetchCategories();
      await fetchPlaceholders();
    } catch (error) {
      console.error('Error creating category:', error);
      toast({
        title: 'Error creating category',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a JPG, PNG, or GIF image',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Set default name from filename without extension
    const fileName = file.name.split('.')[0].replace(/_/g, ' ');
    setUploadName(fileName);
    setUploadFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Upload placeholder image
  const uploadPlaceholder = async () => {
    if (!uploadFile || !selectedCategory) {
      toast({
        title: 'Missing information',
        description: 'Please select a file and category',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsUploading(true);
    
    try {
      // Create a normalized filename
      const fileExt = uploadFile.name.split('.').pop().toLowerCase();
      const normalizedName = uploadName.trim()
        .toLowerCase()
        .replace(/\s+/g, '_') + '.' + fileExt;
      
      // Upload to the selected category
      const path = `athletes/${selectedCategory}/${normalizedName}`;
      
      const { error } = await supabase.storage
        .from('playmakerpics-images')
        .upload(path, uploadFile, {
          cacheControl: '3600',
          upsert: true,
        });
        
      if (error) throw error;
      
      toast({
        title: 'Upload successful',
        description: `Uploaded ${normalizedName} to ${selectedCategory}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Reset form
      setUploadFile(null);
      setUploadName('');
      setPreview('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Refresh placeholders
      await fetchPlaceholders();
    } catch (error) {
      console.error('Error uploading placeholder:', error);
      toast({
        title: 'Upload failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle image deletion
  const promptDeleteImage = (image) => {
    setImageToDelete(image);
    onOpen();
  };

  const confirmDeleteImage = async () => {
    if (!imageToDelete) return;
    
    try {
      const { error } = await supabase.storage
        .from('playmakerpics-images')
        .remove([imageToDelete.path]);
        
      if (error) throw error;
      
      toast({
        title: 'Image deleted',
        description: `Deleted ${imageToDelete.name}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Refresh placeholders
      await fetchPlaceholders();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: 'Delete failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      onClose();
      setImageToDelete(null);
    }
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Heading as="h1" mb={6}>Placeholder Management</Heading>
      <Text mb={4}>
        Upload and manage placeholder images for the template editor. These images will appear in the Images section.
      </Text>
      
      {/* Category Management */}
      <Box bg="gray.50" p={4} borderRadius="md" mb={6}>
        <Heading as="h2" size="md" mb={4}>Categories</Heading>
        <Flex mb={4}>
          <Input 
            placeholder="New category name"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            mr={2}
          />
          <Button 
            colorScheme="blue" 
            onClick={createCategory}
            leftIcon={<AddIcon />}
          >
            Add Category
          </Button>
        </Flex>
        
        <Flex wrap="wrap" gap={2}>
          {categories.map(category => (
            <Badge 
              key={category} 
              colorScheme="blue" 
              p={2} 
              borderRadius="md"
              cursor="pointer"
              onClick={() => setSelectedCategory(category)}
              bg={selectedCategory === category ? "blue.400" : "blue.100"}
              color={selectedCategory === category ? "white" : "blue.800"}
            >
              {category}
            </Badge>
          ))}
          <Badge 
            colorScheme="gray" 
            p={2} 
            borderRadius="md"
            cursor="pointer"
            onClick={() => setSelectedCategory('')}
            bg={selectedCategory === '' ? "gray.400" : "gray.100"}
            color={selectedCategory === '' ? "white" : "gray.800"}
          >
            All Categories
          </Badge>
        </Flex>
      </Box>
      
      {/* Upload Form */}
      <Box bg="gray.50" p={4} borderRadius="md" mb={6}>
        <Heading as="h2" size="md" mb={4}>Upload New Placeholder</Heading>
        
        <Flex direction={{ base: "column", md: "row" }} gap={4}>
          <Box flex="1">
            <FormControl mb={4}>
              <FormLabel>Select Image</FormLabel>
              <Input 
                type="file" 
                accept="image/*"
                onChange={handleFileChange}
                ref={fileInputRef}
              />
            </FormControl>
            
            <FormControl mb={4}>
              <FormLabel>Image Name</FormLabel>
              <Input 
                placeholder="Name for display"
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
              />
            </FormControl>
            
            <FormControl mb={4}>
              <FormLabel>Category</FormLabel>
              <Select 
                placeholder="Select category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </Select>
            </FormControl>
            
            <Button 
              colorScheme="blue" 
              onClick={uploadPlaceholder}
              isLoading={isUploading}
              isDisabled={!uploadFile || !selectedCategory}
              leftIcon={<AddIcon />}
            >
              Upload Image
            </Button>
          </Box>
          
          {preview && (
            <Box flex="1" textAlign="center">
              <Text mb={2}>Preview:</Text>
              <Image 
                src={preview}
                maxH="200px"
                mx="auto"
                borderRadius="md"
                objectFit="contain"
              />
            </Box>
          )}
        </Flex>
      </Box>
      
      {/* Display Placeholders */}
      <Box>
        <Heading as="h2" size="md" mb={4}>Current Placeholders</Heading>
        
        {isLoading ? (
          <Text>Loading placeholders...</Text>
        ) : (
          <>
            {Object.keys(placeholders).map(category => {
              // Skip categories that don't match the filter
              if (selectedCategory && selectedCategory !== category) return null;
              
              return (
                <Box key={category} mb={8}>
                  <Flex align="center" mb={2}>
                    <Heading as="h3" size="sm">{category}</Heading>
                    <Badge ml={2} colorScheme="blue">{placeholders[category].length} images</Badge>
                  </Flex>
                  <Divider mb={4} />
                  
                  {placeholders[category].length === 0 ? (
                    <Text fontStyle="italic">No images in this category</Text>
                  ) : (
                    <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing={4}>
                      {placeholders[category].map(image => (
                        <Box 
                          key={image.path} 
                          borderWidth="1px" 
                          borderRadius="lg" 
                          overflow="hidden"
                          position="relative"
                        >
                          <Image 
                            src={`${image.url}?t=${Date.now()}`} 
                            alt={image.name}
                            objectFit="cover"
                            h="120px"
                            w="100%"
                          />
                          <Box p={2}>
                            <Text fontSize="sm" noOfLines={1}>{image.name}</Text>
                            <Flex mt={2} justifyContent="space-between">
                              <IconButton
                                aria-label="Delete image"
                                icon={<DeleteIcon />}
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => promptDeleteImage(image)}
                              />
                            </Flex>
                          </Box>
                        </Box>
                      ))}
                    </SimpleGrid>
                  )}
                </Box>
              );
            })}
          </>
        )}
      </Box>
      
      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Deletion</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete the image "{imageToDelete?.name}"? 
              This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="gray" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={confirmDeleteImage}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default AdminPlaceholderUpload; 